/****************************************************
 * Module Name:  BidPreview.js
 * Project:      Pansuriya Impex Sales & Manufacturing
 * Owner:        info@pansuriyaimpex.com
 *
 * Copyright (c) Pansuriya Impex
 * All rights reserved.
 ****************************************************/

import { AppBar, Box, Tab, Tabs, Typography } from "@mui/material";
import React from "react";
import NoPermission from "../../common/noPermission";
import SwipeableViews from "react-swipeable-views";
import PropTypes from "prop-types";
import { useTheme } from "@mui/material/styles";
import Constants from "../../../utility/constants";
import StoneWiseBidPreview from "./stoneWiseBidPreview";
import LotWiseBidPreview from "./lotWiseBidPreview";
import FinalBidSummary from "./finalBidSummary";
import DirectStoneWiseBidPreview from "./directStoneWiseBidPreview";
import { GiCutDiamond } from "react-icons/gi";
import { PiDiamondsFourBold } from "react-icons/pi";
import { BsFillInboxesFill } from "react-icons/bs";
import { GiThorHammer } from "react-icons/gi";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <Box
            role="tabpanel"
            hidden={value !== index}
            id={`full-width-tabpanel-${index}`}
            aria-labelledby={`full-width-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 1 }}>
                    <Typography component={"span"}>{children}</Typography>
                </Box>
            )}
        </Box>
    );
}

TabPanel.propTypes = {
    children: PropTypes.node,
    index: PropTypes.number.isRequired,
    value: PropTypes.number.isRequired,
};

function a11yProps(index) {
    return {
        id: `full-width-tab-${index}`,
        "aria-controls": `full-width-tabpanel-${index}`,
        value: index,
    };
}

// fix multiple loops
const BidPreview = () => {
    const theme = useTheme();
    const [value, setValue] = React.useState(
        Constants.USER_PERMISSION.find((iPermission) =>
            iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_STONE) !== undefined ? 0
            : Constants.USER_PERMISSION.find((iPermission) =>
                iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_DIRECTSTONE) !== undefined ? 1
                : Constants.USER_PERMISSION.find((iPermission) =>
                    iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_LOT) !== undefined ? 2
                    : Constants.USER_PERMISSION.find((iPermission) =>
                        iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_FINAL_BID_SUMMARY) !== undefined ? 3 : 4);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleChangeIndex = (index) => {
        setValue(index);
    };

    return (
        value === 4 ? <NoPermission /> :
            <Box sx={{
                bgcolor: "background.paper",
                width: "100%",
                padding: "10px",
                position: "relative"
            }}>
                <AppBar position="static" sx={{ bgcolor: "#03a9f4", height: 50 }}>
                    <Tabs
                        value={value}
                        onChange={handleChange}
                        indicatorColor="secondary"
                        textColor="inherit"
                        variant="fullWidth"
                        aria-label="full width tabs example"
                        sx={{
                            "& .css-1vw5i9y-MuiButtonBase-root-MuiTab-root": {
                                color: "#212121",
                                fontWeight: "bold",
                                textAlign: 'center'
                            },
                            "& .MuiTab-root": {
                                paddingBottom: 4
                            },
                            "& .Mui-selected": {
                                bgcolor: "#0277bd !important",
                                color: "#fafafa !important",
                                fontWeight: "bold !important",
                                paddingBottom: 4
                            }
                        }}
                        TabIndicatorProps={{ style: { background: "#0277bd" } }}
                    >

                        {/* stone */}
                        {Constants.USER_PERMISSION.find(
                            (iPermission) =>
                                iPermission.permissionid ===
                                Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_STONE
                        ) !== undefined ? (
                            <Tab
                                icon={<GiCutDiamond fontSize={"large"} />}
                                iconPosition="start"
                                label="STONE WISE"
                                {...a11yProps(0)}
                            />
                        ) : null}

                        {/*Direct stone */}
                        {Constants.USER_PERMISSION.find(
                            (iPermission) =>
                                iPermission.permissionid ===
                                Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_DIRECTSTONE
                        ) !== undefined ? (
                            <Tab
                                icon={<PiDiamondsFourBold fontSize={"large"} />}
                                iconPosition="start"
                                label="DIRECT STONE WISE"
                                {...a11yProps(1)}
                            />
                        ) : null}

                        {/* lot */}
                        {Constants.USER_PERMISSION.find(
                            (iPermission) =>
                                iPermission.permissionid ===
                                Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_LOT
                        ) !== undefined ? (
                            <Tab
                                icon={<BsFillInboxesFill fontSize={"large"} />}
                                iconPosition="start"
                                label="LOT WISE"
                                {...a11yProps(2)}
                            />
                        ) : null}

                        {/* Final Bid Summary */}
                        {Constants.USER_PERMISSION.find((iPermission) =>
                            iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_FINAL_BID_SUMMARY) !== undefined ? (
                            <Tab
                                icon={<GiThorHammer fontSize={"large"} />}
                                iconPosition="start"
                                label="FINAL BID SUMMARY"
                                {...a11yProps(3)}
                            />
                        ) : null}

                    </Tabs>
                </AppBar>
                <SwipeableViews
                    axis={theme.direction === "rtl" ? "x-reverse" : "x"}
                    index={value}
                    onChangeIndex={handleChangeIndex}
                >
                    {/* stone */}

                    {Constants.USER_PERMISSION.find(
                        (iPermission) =>
                            iPermission.permissionid ===
                            Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_STONE
                    ) !== undefined ? (
                        <TabPanel value={value} index={0}>
                            <StoneWiseBidPreview />
                        </TabPanel>
                    ) : null}

                    {/* Direct stone */}

                    {Constants.USER_PERMISSION.find(
                        (iPermission) =>
                            iPermission.permissionid ===
                            Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_DIRECTSTONE
                    ) !== undefined ? (
                        <TabPanel value={value} index={1}>
                            <DirectStoneWiseBidPreview />
                        </TabPanel>
                    ) : null}

                    {/* lot */}

                    {Constants.USER_PERMISSION.find(
                        (iPermission) =>
                            iPermission.permissionid ===
                            Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_LOT
                    ) !== undefined ? (
                        <TabPanel value={value} index={2}>
                            <LotWiseBidPreview />
                        </TabPanel>

                    ) : null}

                    {/* FINAL BID SUMMARY */}
                    {Constants.USER_PERMISSION.find((iPermission) =>
                        iPermission.permissionid === Constants.PERMISSIONS.TENDER_OPERATION_BID_PREVIEW_FINAL_BID_SUMMARY
                    ) !== undefined ? (
                        <TabPanel value={value} index={3}>
                            <FinalBidSummary />
                        </TabPanel>
                    ) : null}

                </SwipeableViews>

            </Box >
    )
}

export default BidPreview;